/**
 * @file publish.ts
 * @description Общие правила публикации страниц и мест (phase19.16 Р3а;
 * SB126, SB63, SB54, SB128; db-design-r3a §3.2–§3.4, §4.1, §6; решения
 * оркестратора по §9: порог «Страницу правит …» 10 минут, «Вернуть как
 * было» — `keepDraft`).
 *
 * Зачем в shared: коды 409 и форму их `details` читают и api (отвечает), и
 * кабинет (показывает «Пока окно было открыто, страницу изменил Иван
 * (14:40)»); порог «правит другой» и сроки хранения считают api (поле
 * карточки, ночная задача) и кабинет (плашка) — расхождение порогов дало бы
 * плашку без 409 или наоборот. Всё — чистые функции, время параметром.
 */

// ── Коды ошибок (api → кабинет) ──

/**
 * Коды отказов публикации, «Отменить правки» и возврата версии (§3.2, §3.4).
 * `block_changed` (устаревший блок в редакторе) остаётся прежним кодом
 * редактора блоков и здесь не дублируется.
 */
export const SITE_PAGE_ERROR_CODES = {
  /** 409: черновик изменился после того, как окно получило отпечаток. */
  draftChanged: "site_page_draft_changed",
  /** 409: все страницы пачки без правок и уже видны. */
  nothingToPublish: "site_page_nothing_to_publish",
  /** 409: адрес занят другой живой страницей вне пачки. */
  liveSlugTaken: "site_page_live_slug_taken",
  /** 409: гонка двух публикаций (UNIQUE pageId+version). */
  publishConflict: "site_page_publish_conflict",
} as const;

export type SitePageErrorCode = (typeof SITE_PAGE_ERROR_CODES)[keyof typeof SITE_PAGE_ERROR_CODES];

/** Кто последним правил черновик (для 409 и плашки). */
export interface SitePageDraftEditor {
  id: string;
  name: string;
}

/** Одна устаревшая страница в 409 `site_page_draft_changed`: свежий отпечаток, кто и когда правил. */
export interface SitePageDraftChangedPage {
  pageId: string;
  fingerprint: string;
  editedBy: SitePageDraftEditor | null;
  /** ISO-строка (как все DTO). */
  editedAt: string | null;
}

/**
 * `details` ответа 409 `site_page_draft_changed` (§3.2): поля первой
 * устаревшей страницы (совместимость с ответом на одну страницу) и `pages` —
 * все устаревшие страницы пачки (`draftChangedError` в api шлёт его всегда).
 * Необязательно в типе: серверный помощник собирает поля страницы тем же
 * типом без `pages`; кабинет при отсутствии берёт одну страницу из полей.
 */
export interface SitePageDraftChangedDetails extends SitePageDraftChangedPage {
  pages?: SitePageDraftChangedPage[];
}

/** `details` ответа 409 `site_page_live_slug_taken`: какой адрес и чья страница его держит. */
export interface SitePageLiveSlugTakenDetails {
  pageId: string;
  slug: string;
  takenByPageId: string;
}

// ── «Страницу правит …» ──

/** Порог плашки «Страницу сейчас правит …»: последняя правка не старше 10 минут (§9 п. 4). */
export const SITE_PAGE_EDITING_WINDOW_MS = 10 * 60 * 1000;

const toTime = (value: Date | string | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const time = (value instanceof Date ? value : new Date(value)).getTime();
  return Number.isFinite(time) ? time : null;
};

/**
 * Правит ли страницу другой сотрудник: последняя правка не моя и сделана не
 * раньше чем за `SITE_PAGE_EDITING_WINDOW_MS` до `now`. Таблицы присутствия
 * нет (§3.2) — только колонки `draftEditedById/At`. Правка «из будущего»
 * (часы сервера и кабинета разошлись) считается свежей.
 */
export function isSitePageEditedByOther(
  draft: { editedById: string | null; editedAt: Date | string | null },
  currentUserId: string,
  now: Date,
): boolean {
  if (draft.editedById === null || draft.editedById === currentUserId) return false;
  const editedAt = toTime(draft.editedAt);
  if (editedAt === null) return false;
  return now.getTime() - editedAt <= SITE_PAGE_EDITING_WINDOW_MS;
}

// ── Заметки версий ──

/** Автор версий, созданных платформой (перенос при выпуске, §4.1). */
export const SITE_PAGE_SYSTEM_AUTHOR = "Система";

/** Заметки версий, которые пишет сервер (§1.3, §4.1). */
export const SITE_PAGE_REVISION_NOTES = {
  /** Версия 1 из команды site:baseline-pages (SB126 «Перенос при выпуске»). */
  baseline: "Как было на сайте до черновиков",
  /** «Сделать главной» — версия выбранной страницы (SB29). */
  madeHome: "Сделана главной",
} as const;

/** Заметка версии прежней главной: «Перестала быть главной: адрес /x» (§1.3). */
export function sitePageNoLongerHomeNote(newSlug: string): string {
  return `Перестала быть главной: адрес /${newSlug}`;
}

// ── Хранение (SB63, SB54, SB128; §6) ──

export const SITE_PAGE_RETENTION = {
  /** Последние версии каждой страницы хранятся всегда, как бы стары ни были. */
  keepLatest: 10,
  /** Обычный срок версии от публикации. */
  months: 12,
  /** «Условия доставки и оплаты» (юридическая ссылка, SB86/SB54). */
  legalYears: 3,
  /** Мягко удалённая страница хранится столько после удаления, затем удаляется окончательно. */
  deletedPageMonths: 12,
} as const;

/** Дата + N месяцев в UTC (29 февраля + 12 мес. → 1 марта, как `Date`; Postgres дал бы 28 февраля). */
function addMonthsUtc(date: Date, months: number): Date {
  const out = new Date(date.getTime());
  out.setUTCMonth(out.getUTCMonth() + months);
  return out;
}

/**
 * `SitePageRevision.retainUntil` для новой версии: +3 года, если на страницу
 * ведёт юридическая ссылка «Условия доставки и оплаты», иначе +12 месяцев.
 */
export function sitePageRetainUntil(publishedAt: Date, opts: { legalReference: boolean }): Date {
  return addMonthsUtc(
    publishedAt,
    opts.legalReference ? SITE_PAGE_RETENTION.legalYears * 12 : SITE_PAGE_RETENTION.months,
  );
}

/** Когда мягко удалённую страницу можно удалить окончательно (SB128, SB63). */
export function sitePagePurgeAfter(deletedAt: Date): Date {
  return addMonthsUtc(deletedAt, SITE_PAGE_RETENTION.deletedPageMonths);
}

/** Версия для выбора к удалению (строка `SitePageRevision` одной страницы). */
export interface SitePageRetentionRow {
  id: string;
  version: number;
  retainUntil: Date;
}

/**
 * Какие версии ОДНОЙ страницы можно удалить сейчас: срок `retainUntil`
 * прошёл, версия не живая и не входит в `keepLatest` последних по номеру.
 * Порядок входа не важен; ответ — id по возрастанию номера версии.
 */
export function selectExpiredPageRevisions(
  rows: readonly SitePageRetentionRow[],
  liveRevisionId: string | null,
  now: Date,
): string[] {
  const byVersionDesc = [...rows].sort((a, b) => b.version - a.version);
  const protectedIds = new Set(byVersionDesc.slice(0, SITE_PAGE_RETENTION.keepLatest).map((r) => r.id));
  if (liveRevisionId !== null) protectedIds.add(liveRevisionId);
  return byVersionDesc
    .filter((r) => !protectedIds.has(r.id) && r.retainUntil.getTime() < now.getTime())
    .sort((a, b) => a.version - b.version)
    .map((r) => r.id);
}
